import React from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {message} from 'antd';
import type {ProjectRole} from '@/types/api';
import {diagramsApi, projectsApi, type DiagramResponse} from '@/api/projects';

type DiagramEditorDataState = {
	loading: boolean;
	/** 0–100: два параллельных запроса (проект + диаграмма), шаг ~50% за каждый. */
	loadingPercent: number;
	diagram: DiagramResponse | null;
	projectName: string;
	projectRole?: ProjectRole;
	diagramName: string;
	initialXml: string | undefined;
	initialJson: object | string | undefined;
};

export function useDiagramEditorData(): {
	projectId: number;
	diagramId: number;
	data: DiagramEditorDataState;
	setDiagram: (d: DiagramResponse | null) => void;
	setDiagramName: (name: string) => void;
} {
	const {id: projectIdParam, diagramId: diagramIdParam} = useParams<{
		id: string;
		diagramId: string;
	}>();
	const navigate = useNavigate();
	const projectId = projectIdParam ? Number(projectIdParam) : 0;
	const diagramId = diagramIdParam ? Number(diagramIdParam) : 0;

	const [loading, setLoading] = React.useState(true);
	const [loadingPercent, setLoadingPercent] = React.useState(0);
	const [diagram, setDiagram] = React.useState<DiagramResponse | null>(null);
	const [projectName, setProjectName] = React.useState('');
	const [projectRole, setProjectRole] = React.useState<ProjectRole | undefined>(undefined);
	const [diagramName, setDiagramName] = React.useState('');
	const [initialXml, setInitialXml] = React.useState<string | undefined>(undefined);
	const [initialJson, setInitialJson] = React.useState<object | string | undefined>(undefined);

	React.useEffect(() => {
		if (!projectId || !diagramId) return;
		let cancelled = false;
		setLoading(true);
		setLoadingPercent(0);
		let completed = 0;
		const bumpProgress = () => {
			completed += 1;
			if (!cancelled) setLoadingPercent(Math.round((completed / 2) * 100));
		};
		Promise.all([
			projectsApi.get(projectId).then(res => {
				bumpProgress();
				return res;
			}),
			diagramsApi.get(projectId, diagramId).then(res => {
				bumpProgress();
				return res;
			}),
		]).then(([projRes, diagRes]) => {
			if (cancelled) return;
			setLoading(false);
			setLoadingPercent(100);
			if (projRes.error && projRes.status === 401) {
				navigate('/login', {
					state: {from: {pathname: `/projects/${projectId}/diagrams/${diagramId}`}},
					replace: true,
				});
				return;
			}
			if (diagRes.error) {
				message.error(diagRes.error.message || 'Диаграмма не найдена');
				if (diagRes.status === 401) {
					navigate('/login', {
						state: {
							from: {pathname: `/projects/${projectId}/diagrams/${diagramId}`},
						},
						replace: true,
					});
				} else {
					navigate('/', {replace: true});
				}
				return;
			}
			if (projRes.data) {
				setProjectName(projRes.data.name);
				setProjectRole(projRes.data.role);
			}
			if (diagRes.data) {
				setDiagramName(diagRes.data.name);
				setDiagram(diagRes.data);
				const jsonRaw = diagRes.data.modelJson;
				if (jsonRaw) {
					try {
						setInitialJson(typeof jsonRaw === 'string' ? JSON.parse(jsonRaw) : jsonRaw);
					} catch {
						setInitialJson(undefined);
					}
					setInitialXml(undefined);
				} else {
					setInitialJson(undefined);
					setInitialXml(undefined);
				}
			}
		});
		return () => {
			cancelled = true;
		};
	}, [projectId, diagramId, navigate]);

	return {
		projectId,
		diagramId,
		data: {
			loading,
			loadingPercent,
			diagram,
			projectName,
			projectRole,
			diagramName,
			initialXml,
			initialJson,
		},
		setDiagram,
		setDiagramName,
	};
}
